import React, { useState, useEffect, useContext } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  CalendarViewMonth,
  CalendarViewWeek,
  ViewModule
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthContext from "../context/AuthContext";

// Days of the week
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_NARROW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [existingDays, setExistingDays] = useState([]);
  const [view, setView] = useState('month'); // 'month', 'week', 'year'
  const [calMode, setCalMode] = useState('compare'); // 'my' | 'compare'
  const [loading, setLoading] = useState(false);
  const [isLinked, setIsLinked] = useState(false);
  const [endedRelSnackbar, setEndedRelSnackbar] = useState(false);
  const { backendUrl, authTokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get access token from authTokens
  const accessToken = authTokens?.access;

  // Get first and last day of the month
  const getMonthData = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Get the day of week for the first day (0 = Sunday)
    const startingDayOfWeek = firstDay.getDay();

    // Calculate days to show from previous month
    const daysFromPrevMonth = startingDayOfWeek;

    // Calculate days to show from next month
    const totalDays = lastDay.getDate();
    const remainingDays = (7 - ((daysFromPrevMonth + totalDays) % 7)) % 7;

    return {
      firstDay,
      lastDay,
      daysFromPrevMonth,
      totalDays,
      remainingDays,
      year,
      month
    };
  };

  // Fetch data for the visible range
  const fetchDataForRange = async (start, end) => {
    if (!accessToken) {
      return;
    }

    setLoading(true);
    try {
      // Remove trailing slash from backendUrl if it exists
      const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
      const url = `${baseUrl}/api/days?start=${start.toISOString()}&end=${end.toISOString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + String(accessToken),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setExistingDays(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch data, status:', response.status, errorText);
        setExistingDays([]);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setExistingDays([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data based on current view
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let start, end;

    if (view === 'month') {
      const { firstDay, lastDay } = getMonthData(currentDate);
      start = new Date(firstDay);
      start.setDate(start.getDate() - firstDay.getDay());
      end = new Date(lastDay);
      end.setDate(end.getDate() + (6 - lastDay.getDay()));
    } else if (view === 'week') {
      start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay());
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (view === 'year') {
      start = new Date(currentDate.getFullYear(), 0, 1);
      end = new Date(currentDate.getFullYear(), 11, 31);
    }

    fetchDataForRange(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, accessToken, view]);

  // Fetch link status to determine if My View / Compare should be shown
  useEffect(() => {
    if (!accessToken) return;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
    fetch(`${baseUrl}/api/current-relationship/${dateStr}/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(rel => {
        if (!rel?.relationship_id) return;
        return fetch(`${baseUrl}/api/relationship-link-status/${rel.relationship_id}/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      })
      .then(r => r?.ok ? r.json() : null)
      .then(data => { if (data) setIsLinked(data.is_linked); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Navigate to previous period
  const handlePrev = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    } else if (view === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
    }
  };

  // Navigate to next period
  const handleNext = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    } else if (view === 'year') {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
    }
  };

  // Navigate to current period
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Format date to YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle day click
  const handleDayClick = (date) => {
    const formattedDate = formatDateLocal(date);
    const dayData = getDayData(formattedDate);
    if (dayData?.date_ended) {
      setEndedRelSnackbar(true);
      return;
    }
    navigate(`/today?date=${formattedDate}`);
  };

  // Handle month click in year view
  const handleMonthClick = (year, month) => {
    setCurrentDate(new Date(year, month, 1));
    setView('month');
  };

  // Check if a day has data
  const hasData = (dateStr) => {
    return existingDays.some(day => day.date === dateStr);
  };

  // Get day data for a specific date
  const getDayData = (dateStr) => {
    return existingDays.find(day => day.date === dateStr);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Generate week days
  const generateWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }

    return days;
  };

  // Generate calendar days (for month view)
  const generateCalendarDays = () => {
    const { year, month, daysFromPrevMonth, totalDays, remainingDays } = getMonthData(currentDate);
    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(year, month - 1, day);
      days.push(date);
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      days.push(date);
    }

    // Next month days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push(date);
    }

    return days;
  };

  // Count days with data in a month
  const countDaysWithDataInMonth = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let count = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDateLocal(date);
      if (hasData(dateStr)) {
        count++;
      }
    }

    return count;
  };

  // Get title based on view
  const getTitle = () => {
    if (view === 'year') {
      return currentDate.getFullYear().toString();
    } else if (view === 'week') {
      const weekDays = generateWeekDays();
      const start = weekDays[0];
      const end = weekDays[6];
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  // Render month view
  const renderMonthView = () => {
    const calendarDays = generateCalendarDays();

    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Weekday Headers */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {WEEKDAYS.map((day, i) => (
            <Box key={day} sx={{ p: { xs: '4px 0', sm: 1 }, textAlign: 'center', fontWeight: 600, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{day}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{WEEKDAYS_NARROW[i]}</Box>
            </Box>
          ))}
        </Box>

        {/* Calendar Days */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '1fr' }}>
          {calendarDays.map((date, index) => {
            const dateStr = formatDateLocal(date);
            const hasRecord = hasData(dateStr);
            const isTodayDate = isToday(date);
            const inCurrentMonth = isCurrentMonth(date);

            return (
              <Box
                key={index}
                onClick={() => handleDayClick(date)}
                sx={{
                  minHeight: { xs: 58, sm: 80 },
                  p: { xs: '3px 2px', sm: 1 },
                  borderRight: index % 7 !== 6 ? '1px solid' : 'none',
                  borderBottom: index < calendarDays.length - 7 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  backgroundColor: hasRecord ? (theme) => alpha(theme.palette.success.main, 0.08) : 'transparent',
                  '&:hover': {
                    backgroundColor: hasRecord ? (theme) => alpha(theme.palette.success.main, 0.15) : (theme) => alpha(theme.palette.primary.main, 0.05),
                    transform: 'scale(1.02)',
                    zIndex: 1,
                    boxShadow: 2
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  borderRadius: '50%',
                  backgroundColor: isTodayDate ? 'primary.main' : 'transparent',
                  color: isTodayDate ? 'primary.contrastText' : inCurrentMonth ? 'text.primary' : 'text.disabled',
                  fontWeight: isTodayDate ? 700 : inCurrentMonth ? 600 : 400,
                  fontSize: { xs: '0.6rem', sm: '0.75rem' },
                  mb: 0.5
                }}>
                  {date.getDate()}
                </Box>

                {hasRecord && (
                  <Box sx={{ position: 'absolute', bottom: { xs: 3, sm: 5 }, right: { xs: 3, sm: 5 }, width: { xs: 5, sm: 6 }, height: { xs: 5, sm: 6 }, borderRadius: '50%', backgroundColor: 'success.main', boxShadow: 1 }} />
                )}

                {/* Winner Indicators - Corner Triangles */}
                {hasRecord && (() => {
                  const dayData = getDayData(dateStr);
                  if (!dayData) return null;

                  // Use cross-comparison data when available, else fall back to my recording
                  const myChores = dayData.user_chore_weight || 0;
                  const partnerChores = (calMode === 'compare' && dayData.cross_data_available)
                    ? (dayData.partner_recorded_chore_weight || 0)
                    : (dayData.partner_chore_weight || 0);

                  let choresWinner = null;
                  if (myChores < partnerChores) choresWinner = 'user';
                  else if (partnerChores < myChores) choresWinner = 'partner';

                  const myTimeoff = dayData.user_timeoff_hours || 0;
                  const partnerTimeoff = (calMode === 'compare' && dayData.cross_data_available)
                    ? (dayData.partner_recorded_timeoff_hours || 0)
                    : (dayData.partner_timeoff_hours || 0);

                  let timeoffWinner = null;
                  if (myTimeoff > partnerTimeoff) timeoffWinner = 'user';
                  else if (partnerTimeoff > myTimeoff) timeoffWinner = 'partner';

                  return (
                    <>
                      {choresWinner && (
                        <Box sx={{
                          position: 'absolute', top: 0, left: 0, width: 0, height: 0,
                          borderTop: '16px solid', borderRight: '16px solid transparent',
                          borderTopColor: choresWinner === 'user' ? '#4caf50' : '#9c27b0',
                          opacity: 0.7
                        }} />
                      )}
                      {timeoffWinner && (
                        <Box sx={{
                          position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                          borderTop: '16px solid', borderLeft: '16px solid transparent',
                          borderTopColor: timeoffWinner === 'user' ? '#4caf50' : '#9c27b0',
                          opacity: 0.7
                        }} />
                      )}
                    </>
                  );
                })()}

                <Box sx={{ mt: 0.5, minHeight: { xs: 0, sm: 30 }, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.25 }}>
                  {hasRecord && (() => {
                    const dayData = getDayData(dateStr);
                    if (!dayData) return null;

                    const partnerChores = (calMode === 'compare' && dayData.cross_data_available)
                      ? dayData.partner_recorded_chore_weight
                      : dayData.partner_chore_weight;
                    const partnerTimeoff = (calMode === 'compare' && dayData.cross_data_available)
                      ? dayData.partner_recorded_timeoff_hours
                      : dayData.partner_timeoff_hours;
                    const fmt = (v) => v == null ? '?' : (v % 1 === 0 ? v : v.toFixed(1));

                    return (
                      <Box sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        {(dayData.user_chore_weight > 0 || partnerChores > 0) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                              Chores:
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                              {fmt(dayData.user_chore_weight)} / {fmt(partnerChores)}
                              {calMode === 'compare' && dayData.cross_data_available && (
                                <Box component="span" sx={{ ml: 0.25, opacity: 0.5 }} title="Partner's own recording">✓</Box>
                              )}
                            </Typography>
                          </Box>
                        )}
                        {(dayData.user_timeoff_hours > 0 || partnerTimeoff > 0) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                              Time-off:
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                              {dayData.user_timeoff_hours}h / {partnerTimeoff != null ? `${partnerTimeoff}h` : '?'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekDays = generateWeekDays();

    // Mobile: vertical stacked day cards
    if (isMobile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {weekDays.map((date, index) => {
            const dateStr = formatDateLocal(date);
            const hasRecord = hasData(dateStr);
            const isTodayDate = isToday(date);
            const dayData = getDayData(dateStr);

            const partnerChores = (calMode === 'compare' && dayData?.cross_data_available)
              ? dayData?.partner_recorded_chore_weight
              : dayData?.partner_chore_weight;
            const partnerTimeoff = (calMode === 'compare' && dayData?.cross_data_available)
              ? dayData?.partner_recorded_timeoff_hours
              : dayData?.partner_timeoff_hours;
            const fmt = (v) => v == null ? '?' : (v % 1 === 0 ? v : v.toFixed(1));

            const myChores = dayData?.user_chore_weight || 0;
            const myTimeoff = dayData?.user_timeoff_hours || 0;
            const pChores = (dayData?.cross_data_available ? dayData?.partner_recorded_chore_weight : dayData?.partner_chore_weight) || 0;
            const pTimeoff = (dayData?.cross_data_available ? dayData?.partner_recorded_timeoff_hours : dayData?.partner_timeoff_hours) || 0;
            let choresWinner = null;
            if (myChores < pChores) choresWinner = 'user';
            else if (pChores < myChores) choresWinner = 'partner';
            let timeoffWinner = null;
            if (myTimeoff > pTimeoff) timeoffWinner = 'user';
            else if (pTimeoff > myTimeoff) timeoffWinner = 'partner';

            return (
              <Paper
                key={index}
                elevation={0}
                onClick={() => handleDayClick(date)}
                sx={{
                  border: '1.5px solid',
                  borderColor: isTodayDate ? 'primary.main' : hasRecord ? 'success.light' : 'divider',
                  borderRadius: 2,
                  p: 1.5,
                  cursor: 'pointer',
                  backgroundColor: hasRecord ? (t) => alpha(t.palette.success.main, 0.06) : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: hasRecord ? (t) => alpha(t.palette.success.main, 0.12) : (t) => alpha(t.palette.primary.main, 0.05),
                    boxShadow: 2,
                  }
                }}
              >
                {/* Corner triangles */}
                {hasRecord && choresWinner && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, borderTop: '18px solid', borderRight: '18px solid transparent', borderTopColor: choresWinner === 'user' ? '#4caf50' : '#9c27b0', opacity: 0.7 }} />
                )}
                {hasRecord && timeoffWinner && (
                  <Box sx={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderTop: '18px solid', borderLeft: '18px solid transparent', borderTopColor: timeoffWinner === 'user' ? '#4caf50' : '#9c27b0', opacity: 0.7 }} />
                )}

                {/* Day label + number */}
                <Box sx={{ textAlign: 'center', minWidth: 44, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                    {WEEKDAYS[date.getDay()]}
                  </Typography>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', mx: 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: isTodayDate ? 'primary.main' : 'transparent',
                    color: isTodayDate ? 'primary.contrastText' : 'text.primary',
                    fontWeight: isTodayDate ? 700 : 600,
                    fontSize: '1rem',
                  }}>
                    {date.getDate()}
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  {hasRecord && dayData ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(dayData.user_chore_weight > 0 || partnerChores > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 52 }}>Chores:</Typography>
                          <Typography variant="caption">{fmt(dayData.user_chore_weight)} / {fmt(partnerChores)}</Typography>
                          {calMode === 'compare' && dayData.cross_data_available && (
                            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.65rem' }}>✓</Typography>
                          )}
                        </Box>
                      )}
                      {(dayData.user_timeoff_hours > 0 || partnerTimeoff > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 52 }}>Time-off:</Typography>
                          <Typography variant="caption">{dayData.user_timeoff_hours}h / {partnerTimeoff != null ? `${partnerTimeoff}h` : '?'}</Typography>
                        </Box>
                      )}
                      {dayData.user_chore_weight === 0 && !partnerChores && dayData.user_timeoff_hours === 0 && !partnerTimeoff && (
                        <Typography variant="caption" color="text.secondary">Activity recorded</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">No activity</Typography>
                  )}
                </Box>

                {/* Status dot */}
                {hasRecord && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'success.main', flexShrink: 0 }} />
                )}
              </Paper>
            );
          })}
        </Box>
      );
    }

    // Desktop: horizontal 7-column grid
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {WEEKDAYS.map((day) => (
            <Box key={day} sx={{ p: 2, textAlign: 'center', fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {day}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {weekDays.map((date, index) => {
            const dateStr = formatDateLocal(date);
            const hasRecord = hasData(dateStr);
            const isTodayDate = isToday(date);

            return (
              <Box
                key={index}
                onClick={() => handleDayClick(date)}
                sx={{
                  minHeight: 200,
                  p: 2,
                  borderRight: index < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  backgroundColor: hasRecord ? (t) => alpha(t.palette.success.main, 0.08) : 'transparent',
                  '&:hover': {
                    backgroundColor: hasRecord ? (t) => alpha(t.palette.success.main, 0.15) : (t) => alpha(t.palette.primary.main, 0.05),
                    transform: 'scale(1.02)',
                    zIndex: 1,
                    boxShadow: 2
                  }
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: isTodayDate ? 'primary.main' : 'transparent',
                  color: isTodayDate ? 'primary.contrastText' : 'text.primary',
                  fontWeight: isTodayDate ? 700 : 600,
                  fontSize: '1rem',
                  mb: 2
                }}>
                  {date.getDate()}
                </Box>

                {hasRecord && (
                  <Box sx={{ position: 'absolute', bottom: 8, right: 8, width: 10, height: 10, borderRadius: '50%', backgroundColor: 'success.main', boxShadow: 1 }} />
                )}

                {/* Winner Indicators - Corner Triangles */}
                {hasRecord && (() => {
                  const dayData = getDayData(dateStr);
                  if (!dayData) return null;

                  const myChores = dayData.user_chore_weight || 0;
                  const partnerChores = dayData.cross_data_available
                    ? (dayData.partner_recorded_chore_weight || 0)
                    : (dayData.partner_chore_weight || 0);
                  let choresWinner = null;
                  if (myChores < partnerChores) choresWinner = 'user';
                  else if (partnerChores < myChores) choresWinner = 'partner';

                  const myTimeoff = dayData.user_timeoff_hours || 0;
                  const partnerTimeoff = dayData.cross_data_available
                    ? (dayData.partner_recorded_timeoff_hours || 0)
                    : (dayData.partner_timeoff_hours || 0);
                  let timeoffWinner = null;
                  if (myTimeoff > partnerTimeoff) timeoffWinner = 'user';
                  else if (partnerTimeoff > myTimeoff) timeoffWinner = 'partner';

                  return (
                    <>
                      {choresWinner && (
                        <Box sx={{
                          position: 'absolute', top: 0, left: 0, width: 0, height: 0,
                          borderTop: '20px solid', borderRight: '20px solid transparent',
                          borderTopColor: choresWinner === 'user' ? '#4caf50' : '#9c27b0',
                          opacity: 0.7
                        }} />
                      )}
                      {timeoffWinner && (
                        <Box sx={{
                          position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                          borderTop: '20px solid', borderLeft: '20px solid transparent',
                          borderTopColor: timeoffWinner === 'user' ? '#4caf50' : '#9c27b0',
                          opacity: 0.7
                        }} />
                      )}
                    </>
                  );
                })()}

                <Box sx={{ mt: 1 }}>
                  {hasRecord && (() => {
                    const dayData = getDayData(dateStr);
                    if (!dayData) return null;

                    const partnerChores = (calMode === 'compare' && dayData.cross_data_available)
                      ? dayData.partner_recorded_chore_weight
                      : dayData.partner_chore_weight;
                    const partnerTimeoff = (calMode === 'compare' && dayData.cross_data_available)
                      ? dayData.partner_recorded_timeoff_hours
                      : dayData.partner_timeoff_hours;
                    const fmt = (v) => v == null ? '?' : (v % 1 === 0 ? v : v.toFixed(1));

                    return (
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {(dayData.user_chore_weight > 0 || partnerChores > 0) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Chores:</Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                              {fmt(dayData.user_chore_weight)} / {fmt(partnerChores)}
                              {calMode === 'compare' && dayData.cross_data_available && (
                                <Box component="span" sx={{ ml: 0.5, opacity: 0.5, fontSize: '0.65rem' }} title="Partner's own recording">✓</Box>
                              )}
                            </Typography>
                          </Box>
                        )}
                        {(dayData.user_timeoff_hours > 0 || partnerTimeoff > 0) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Time-off:</Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                              {dayData.user_timeoff_hours}h / {partnerTimeoff != null ? `${partnerTimeoff}h` : '?'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // Render year view
  const renderYearView = () => {
    const year = currentDate.getFullYear();

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(240px, 1fr))' }, gap: { xs: 1.5, sm: 3 } }}>
        {MONTHS.map((monthName, monthIndex) => {
          const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
          const daysWithData = countDaysWithDataInMonth(year, monthIndex);
          const progress = daysInMonth > 0 ? (daysWithData / daysInMonth) * 100 : 0;

          return (
            <Paper
              key={monthIndex}
              elevation={0}
              onClick={() => handleMonthClick(year, monthIndex)}
              sx={{
                p: { xs: 1.5, sm: 3 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                  borderColor: 'primary.main'
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {monthName}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {daysWithData} of {daysInMonth} days with activities
                </Typography>
                <Box sx={{
                  width: '100%',
                  height: 8,
                  backgroundColor: (theme) => alpha(theme.palette.grey[300], 0.3),
                  borderRadius: 1,
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: 'success.main',
                    transition: 'width 0.3s ease'
                  }} />
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Click to view details
              </Typography>
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <>
    <SEO title="Calendar - Couplendar" />
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1400, mx: 'auto', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        {/* Row 1: Title + right controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: '#7B6B8D', fontSize: { xs: '1.2rem', sm: '2.125rem' } }}
          >
            {getTitle()}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Mode Toggle — desktop only in this row */}
            {isLinked && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1 }}>
                <ToggleButtonGroup
                  value={calMode}
                  exclusive
                  onChange={(_, v) => v && setCalMode(v)}
                  size="small"
                  sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
                >
                  <ToggleButton value="my" sx={{ textTransform: 'none', fontWeight: 500, px: 2, fontSize: '0.875rem', '&.Mui-selected': { bgcolor: '#7B6B8D', color: 'white', '&:hover': { bgcolor: '#6A5A7A' } } }}>
                    My View
                  </ToggleButton>
                  <ToggleButton value="compare" sx={{ textTransform: 'none', fontWeight: 500, px: 2, fontSize: '0.875rem', '&.Mui-selected': { bgcolor: '#7B6B8D', color: 'white', '&:hover': { bgcolor: '#6A5A7A' } } }}>
                    Compare
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* View Toggle */}
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, newView) => newView && setView(newView)}
              size="small"
            >
              <ToggleButton value="month">
                <Tooltip title="Month View"><CalendarViewMonth /></Tooltip>
              </ToggleButton>
              <ToggleButton value="week">
                <Tooltip title="Week View"><CalendarViewWeek /></Tooltip>
              </ToggleButton>
              <ToggleButton value="year">
                <Tooltip title="Year View"><ViewModule /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Navigation */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={`Previous ${view}`}>
                <IconButton onClick={handlePrev} size="small"><ChevronLeft /></IconButton>
              </Tooltip>
              <Tooltip title="Today">
                <IconButton onClick={handleToday} size="small" color="primary"><TodayIcon /></IconButton>
              </Tooltip>
              <Tooltip title={`Next ${view}`}>
                <IconButton onClick={handleNext} size="small"><ChevronRight /></IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Row 2: Mode toggle (mobile only, centred below title row) */}
        {isLinked && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mt: 1 }}>
            <ToggleButtonGroup
              value={calMode}
              exclusive
              onChange={(_, v) => v && setCalMode(v)}
              size="small"
              sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
            >
              <ToggleButton value="my" sx={{ textTransform: 'none', fontWeight: 500, px: 1.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: '#7B6B8D', color: 'white', '&:hover': { bgcolor: '#6A5A7A' } } }}>
                My View
              </ToggleButton>
              <ToggleButton value="compare" sx={{ textTransform: 'none', fontWeight: 500, px: 1.5, fontSize: '0.75rem', '&.Mui-selected': { bgcolor: '#7B6B8D', color: 'white', '&:hover': { bgcolor: '#6A5A7A' } } }}>
                Compare
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>

      {/* Calendar Views */}
      <Box sx={{ flex: 1, overflow: 'auto', mb: 2, position: 'relative' }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            height: '100%'
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'year' && renderYearView()}
          </>
        )}
      </Box>

      {/* Legend */}
      {view !== 'year' && (
        <Box sx={{
          mt: 2,
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2 },
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 1.5, fontSize: '0.8rem' }}>
            How to read this calendar
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>

            {/* Day indicators */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                Day Markers
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#7B6B8D', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">Today's date</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: (t) => alpha(t.palette.success.main, 0.15), border: '1.5px solid', borderColor: 'success.main', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">Day has recorded activity</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">Green dot = activity exists</Typography>
                </Box>
              </Box>
            </Box>

            {/* Numbers */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                Numbers on Each Day
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                      <Box component="span" sx={{ color: '#7B6B8D' }}>2.5</Box>
                      {' / '}
                      <Box component="span" sx={{ color: '#C4856A' }}>1</Box>
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Chore weight: <Box component="span" sx={{ color: '#7B6B8D', fontWeight: 600 }}>you</Box> / <Box component="span" sx={{ color: '#C4856A', fontWeight: 600 }}>partner</Box></Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>3h / 0h</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Time-off hours: you / partner</Typography>
                </Box>
              </Box>
            </Box>

            {/* Triangles */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                Corner Triangles
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 0, height: 0, borderTop: '12px solid #4caf50', borderRight: '12px solid transparent', opacity: 0.8, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary"><strong>Top-left green</strong> = you did fewer chores that day</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 0, height: 0, borderTop: '12px solid #9c27b0', borderRight: '12px solid transparent', opacity: 0.8, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary"><strong>Top-left purple</strong> = partner did fewer chores</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 0, height: 0, borderTop: '12px solid #4caf50', borderLeft: '12px solid transparent', opacity: 0.8, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary"><strong>Top-right green</strong> = you had more time-off</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 0, height: 0, borderTop: '12px solid #9c27b0', borderLeft: '12px solid transparent', opacity: 0.8, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary"><strong>Top-right purple</strong> = partner had more time-off</Typography>
                </Box>
              </Box>
            </Box>

            {/* Modes — only shown when relationship is linked */}
            {isLinked && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
                  View Modes
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ bgcolor: '#7B6B8D', borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 600 }}>My View</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Your recordings of both you and your partner (includes private items)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ bgcolor: '#7B6B8D', borderRadius: 1, px: 0.75, py: 0.25, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.65rem', fontWeight: 600 }}>Compare</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Your data vs partner's independently logged data (shared items only)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.75rem', flexShrink: 0 }}>✓</Typography>
                    <Typography variant="caption" color="text.secondary">Partner's own recording is available for that day</Typography>
                  </Box>
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      )}
    </Box>
    <Snackbar
      open={endedRelSnackbar}
      autoHideDuration={4000}
      onClose={() => setEndedRelSnackbar(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="info" onClose={() => setEndedRelSnackbar(false)}>
        This day belongs to an ended relationship and cannot be viewed or edited.
      </Alert>
    </Snackbar>
    </>
  );
}

export default CalendarPage;
